import * as React from "react";
import { useEffect } from "react";
import { Box } from "@mui/material";
import Header from "./Header";
import { MediaDevicesContext, MediaDevicesHandler, useSession } from "@dvrt/web-phone-client";
import { Audio, useLogger } from "@dvrt/web-phone-client";
import { WebClientUISettings } from "../contexts/selectedDevices";
import { RoomSettings } from "./context";
import Sidebar from "./sidebar/Container";
import Client from "./client/ClientContainer";
import { useParticipantSpeakerCalculator } from "./hooks";
import { useDebugSettings } from "conference/tools";
import { ChatMessagesContextProvider } from "./chatMessages";
import { ParticipantsStatusContextProvider } from "./participantStatus";
import { useMuteStateSynchronizer } from "./muteStateSynchronizer";
import SpeakerListInfoDialog from "./SpeakerListInfoDialog";
import { FileUploaderFactoryContextProvider } from "./fileUploader";
import { SelfVideoDialogWrapper } from "./self-video/SelfVideoDialog";
import { SelfVideoDialogSettingsContext } from "./self-video/context";
import { VideoEffectContextProvider } from "../contexts/videoEffectContext";
import { useDebugControlPositionChanger } from "conference/components/debug/tools";
import ParticipantGrid from "./session/grid/ParticipantGrid";
import { FormattedMessage } from "react-intl";

const MainContent = () => {
    useDebugControlPositionChanger({ top: 15, left: 50 });

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <ChatMessagesContextProvider>
                <FileUploaderFactoryContextProvider>
                    <ParticipantsStatusContextProvider>
                        <SelfVideoDialogSettingsContext.Provider>
                            <Header />
                            <SessionContent />
                            <SelfVideoDialogWrapper />
                        </SelfVideoDialogSettingsContext.Provider>
                    </ParticipantsStatusContextProvider>
                </FileUploaderFactoryContextProvider>
            </ChatMessagesContextProvider>
        </Box>
    );
};

function SessionMain({ style }: { style?: React.CSSProperties }) {
    const context = useSession();

    const logger = useLogger("SessionContent");
    logger.debug(
        "rendering video tracks: %s, audio tracks: %s",
        context.remoteVideoTracks.length,
        context.remoteAudioTracks.length,
    );

    if (context.participants.length === 50)
        return (
            <div style={{ textAlign: "center", ...style }}>
                <FormattedMessage
                    id="meetingPage.room.alone"
                    defaultMessage="Looks like you are alone in this meeting."
                />
            </div>
        );

    return (
        <>
            <ParticipantGrid participants={context.participants} />
            {context.remoteAudioTracks.map((track) => (
                <Audio track={track} key={track.id} />
            ))}
        </>
    );
}

const SessionContent = () => {
    useParticipantSpeakerCalculator();
    useMuteStateSynchronizer();

    return (
        <Box
            sx={{
                width: "100%",
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    flexGrow: 1,
                    padding: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <SessionMain />
                <SpeakerListInfoDialog />
            </Box>
            <Sidebar />
        </Box>
    );
};

const RoomContainer = () => {
    const mediaDevicesHandler = new MediaDevicesHandler();
    const debugSettings = useDebugSettings();

    useEffect(() => {
        window.onbeforeunload = debugSettings.enabled && debugSettings.disableRoomLeaveBlocker ? null : () => "";
        return () => (window.onbeforeunload = null);
    }, [debugSettings.enabled, debugSettings.disableRoomLeaveBlocker]);

    return (
        <VideoEffectContextProvider>
            <MediaDevicesContext.Provider value={mediaDevicesHandler}>
                <WebClientUISettings>
                    <RoomSettings.Provider>
                        <Client>
                            <MainContent />
                        </Client>
                    </RoomSettings.Provider>
                </WebClientUISettings>
            </MediaDevicesContext.Provider>
        </VideoEffectContextProvider>
    );
};

export default RoomContainer;
